/**
 * Shop API Service Client for frontend-v2
 * Consumes official Fortnite API / local FastAPI / cache fallback
 * Supporting exact layout tokens, ranks, and currencies.
 */

import { RawShopEntry } from '../utils/fortniteShopParser';
import { ShopItem, SupportedCurrency } from '../types';
import { sanitizeCurrency, calculateFiatPrice } from '../utils/currency';

export interface RawBackendShopItem {
  id: string;
  name: string;
  description?: string;
  vbucks: number;
  price?: number;
  currency?: string;
  image?: string;
  images?: {
    icon?: string;
    featured?: string;
    background?: string;
  };
  rarity?: string;
  category?: string;
  section?: string;
  tags?: string[];
  isBundle?: boolean;
}

export interface ShopApiResponse {
  count: number;
  currency: SupportedCurrency;
  rate_per_1000: number;
  discount_tiers: Array<{ min_vbucks: number; discount_pct: number }>;
  categories: Record<string, number>;
  sections: string[];
  items: ShopItem[];
  isFallback?: boolean;
  error?: string | null;
}

export interface CartSummaryResponse {
  vbucks: number;
  currency: SupportedCurrency;
  base_price: number;
  discount_pct: number;
  discount_amount: number;
  final_price: number;
}

export interface FetchShopOptions {
  signal?: AbortSignal;
  timeoutMs?: number;
  includeRecharges?: boolean;
}

export const VERIFIED_RECHARGE_OFFERS: ShopItem[] = [
  {
    id: 'recharge_vbucks_12500',
    name: 'Recarga 13.500 Pavos',
    description: 'Carga directa oficial a tu cuenta Epic Games. Sin esperas de bot ni límite de regalos.',
    vbucks: 13500,
    regularPrice: 13500,
    finalPrice: 13500,
    price: 89999,
    currency: 'ARS',
    image: 'https://fortnite-api.com/images/vbucksicon.png',
    images: {
      icon: 'https://fortnite-api.com/images/vbucksicon.png',
      featured: 'https://fortnite-api.com/images/vbucksicon.png',
    },
    rarity: 'mythic',
    category: 'LOTES',
    section: 'Recargas Directas',
    isBundle: true,
    tags: ['Recarga', 'Directa', 'Sin Espera'],
  },
  {
    id: 'recharge_crew',
    name: 'Club de Fortnite (Crew Pack)',
    description: 'Pase de batalla de la temporada actual, 1.000 Pavos y paquete de cosméticos mensual del Club.',
    vbucks: 800,
    regularPrice: 800,
    finalPrice: 800,
    price: 5500,
    currency: 'ARS',
    image: 'https://fortnite-api.com/images/cosmetics/br/cid_crew_featured/icon.png',
    images: {
      icon: 'https://fortnite-api.com/images/cosmetics/br/cid_crew_featured/icon.png',
      featured: 'https://fortnite-api.com/images/cosmetics/br/cid_crew_featured/icon.png',
    },
    rarity: 'epic',
    category: 'LOTES',
    section: 'Recargas Directas',
    isBundle: true,
    tags: ['Club de Fortnite', 'Suscripción', 'Mensual'],
  },
];

export const FALLBACK_CATALOG_ITEMS: ShopItem[] = [
  {
    id: 'outfit_dragon_warrior',
    name: 'Son Goku',
    description: 'Guerrero Saiyajin criado en la Tierra.',
    vbucks: 2000,
    price: 14998,
    currency: 'ARS',
    image: 'https://fortnite-api.com/images/cosmetics/br/cid_a_457_athena_commando_m_cirrusvine/icon.png',
    images: {
      icon: 'https://fortnite-api.com/images/cosmetics/br/cid_a_457_athena_commando_m_cirrusvine/icon.png',
      featured: 'https://fortnite-api.com/images/cosmetics/br/cid_a_457_athena_commando_m_cirrusvine/icon.png',
    },
    rarity: 'legendary',
    series: 'anime',
    category: 'SKINS',
    section: 'Destacados',
    tags: ['Destacado', 'Anime', 'Dragon Ball'],
  },
  {
    id: 'emote_kamehameha',
    name: 'Kamehameha',
    description: '¡Concentra tu ki y libéralo!',
    vbucks: 500,
    price: 3749.5,
    currency: 'ARS',
    image: 'https://fortnite-api.com/images/cosmetics/br/eid_kamehameha/icon.png',
    images: {
      icon: 'https://fortnite-api.com/images/cosmetics/br/eid_kamehameha/icon.png',
    },
    rarity: 'epic',
    category: 'GESTOS',
    section: 'Destacados',
    tags: ['Gesto', 'Popular', 'Anime'],
  },
  {
    id: 'pickaxe_flying_nimbus',
    name: 'Báculo Sagrado',
    description: 'Obsequio sagrado del Maestro Karin.',
    vbucks: 800,
    price: 5999.2,
    currency: 'ARS',
    image: 'https://fortnite-api.com/images/cosmetics/br/pickaxe_id_powershifter/icon.png',
    images: {
      icon: 'https://fortnite-api.com/images/cosmetics/br/pickaxe_id_powershifter/icon.png',
    },
    rarity: 'rare',
    category: 'PICOS',
    section: 'Diario',
    tags: ['Herramienta', 'Dragon Ball'],
  },
  {
    id: 'backpack_power_pole',
    name: 'Caparazón del Duende',
    description: 'Entrenamiento pesado.',
    vbucks: 400,
    price: 2999.6,
    currency: 'ARS',
    image: 'https://fortnite-api.com/images/cosmetics/br/bid_kame_turtle/icon.png',
    images: {
      icon: 'https://fortnite-api.com/images/cosmetics/br/bid_kame_turtle/icon.png',
    },
    rarity: 'uncommon',
    category: 'MOCHILAS',
    section: 'Diario',
    tags: ['Accesorio'],
  },
  {
    id: 'bundle_saiyan_pride',
    name: 'Lote Saiyajin Legendario',
    description: 'Incluye a Son Goku y Vegeta.',
    vbucks: 2800,
    price: 20997.2,
    currency: 'ARS',
    image: 'https://fortnite-api.com/images/cosmetics/br/cid_a_458_athena_commando_m_cirrusvineprince/icon.png',
    images: {
      icon: 'https://fortnite-api.com/images/cosmetics/br/cid_a_458_athena_commando_m_cirrusvineprince/icon.png',
    },
    rarity: 'gaming',
    series: 'gaming',
    category: 'LOTES',
    section: 'Destacados',
    isBundle: true,
    tags: ['Lote', 'Oferta', 'Pack'],
  },
  {
    id: 'glider_crimson_wings',
    name: 'Alas Carmesí',
    description: 'Desciende sobre la isla.',
    vbucks: 1200,
    price: 8998.8,
    currency: 'ARS',
    image: 'https://fortnite-api.com/images/cosmetics/br/glider_id_001/icon.png',
    images: {
      icon: 'https://fortnite-api.com/images/cosmetics/br/glider_id_001/icon.png',
    },
    rarity: 'epic',
    category: 'ALAS DELTA',
    section: 'Originales de Fortnite',
    tags: ['Planeador'],
  },
  ...VERIFIED_RECHARGE_OFFERS,
];

export function normalizeShopItem(raw: RawBackendShopItem, currency: SupportedCurrency): ShopItem {
  const safeCurrency = sanitizeCurrency(currency);
  const primaryImage =
    raw.image ||
    raw.images?.icon ||
    raw.images?.featured ||
    'https://fortnite-api.com/images/vbucksicon.png';

  const rawCat = (raw.category || 'Destacados').trim();
  const rawId = String(raw.id || `item_${Math.random()}`);

  const isBundle =
    Boolean(raw.isBundle) ||
    rawCat.toLowerCase().includes('lote') ||
    rawCat.toLowerCase().includes('bundle') ||
    rawId.toLowerCase().includes('bundle');

  const normalizedCategory = isBundle ? 'LOTES' : rawCat.toUpperCase();

  const fiatPrice =
    typeof raw.price === 'number' && Number.isFinite(raw.price) && raw.currency === safeCurrency
      ? raw.price
      : calculateFiatPrice(raw.vbucks, safeCurrency, rawId);

  return {
    id: rawId,
    name: raw.name || 'Cosmético de Fortnite',
    description: raw.description || 'Cosmético oficial de Fortnite Battle Royale.',
    vbucks: Number.isFinite(raw.vbucks) ? raw.vbucks : 0,
    regularPrice: raw.vbucks,
    finalPrice: raw.vbucks,
    price: fiatPrice,
    currency: safeCurrency,
    image: primaryImage,
    images: {
      icon: raw.images?.icon || primaryImage,
      featured: raw.images?.featured || primaryImage,
      background: raw.images?.background,
    },
    rarity: raw.rarity || 'Raro',
    category: normalizedCategory,
    section: raw.section || 'Destacados',
    tags:
      Array.isArray(raw.tags) && raw.tags.length > 0
        ? raw.tags
        : [normalizedCategory, raw.rarity || 'Oficial', isBundle ? 'Lote' : undefined].filter(
            Boolean
          ) as string[],
    isBundle,
  };
}

export function getFallbackShopData(currency: SupportedCurrency): ShopApiResponse {
  const safeCurrency = sanitizeCurrency(currency);
  const items = FALLBACK_CATALOG_ITEMS.map((item) => ({
    ...item,
    currency: safeCurrency,
    price: calculateFiatPrice(item.vbucks, safeCurrency, item.id),
  }));

  const categories: Record<string, number> = {};
  const sectionsSet = new Set<string>();

  items.forEach((item) => {
    const cat = item.category || 'DESTACADOS';
    categories[cat] = (categories[cat] || 0) + 1;
    if (item.section) sectionsSet.add(item.section);
  });

  return {
    count: items.length,
    currency: safeCurrency,
    rate_per_1000: safeCurrency === 'ARS' ? 7499.0 : 92.0,
    discount_tiers: [],
    categories,
    sections: Array.from(sectionsSet),
    items,
    isFallback: true,
    error: 'Modo sin conexión: mostrando catálogo local de respaldo.',
  };
}

export async function fetchCartSummary(
  vbucks: number,
  currency: SupportedCurrency = 'ARS'
): Promise<CartSummaryResponse> {
  const safeCurrency = sanitizeCurrency(currency);
  const basePrice = calculateFiatPrice(vbucks, safeCurrency);
  return {
    vbucks,
    currency: safeCurrency,
    base_price: basePrice,
    discount_pct: 0,
    discount_amount: 0,
    final_price: basePrice,
  };
}

export async function fetchShop(
  currency: SupportedCurrency = 'ARS',
  options: FetchShopOptions = {}
): Promise<ShopApiResponse> {
  const safeCurrency = sanitizeCurrency(currency);
  const rawResult = await fetchRawShopEntries(safeCurrency, { signal: options.signal });
  if (rawResult.entries.length === 0) {
    return getFallbackShopData(safeCurrency);
  }

  const items = rawResult.entries.map((entry) => {
    const rate = safeCurrency === 'MXN' ? 0.092 : 7.499;
    return normalizeShopItem(
      {
        id: entry.offerId,
        name: entry.devName || 'Cosmético',
        vbucks: entry.finalPrice,
        price: Math.round(entry.finalPrice * rate),
        currency: safeCurrency,
        category: entry.bundle ? 'LOTES' : 'SKINS',
        section: entry.layout?.name || 'Destacados',
        isBundle: Boolean(entry.bundle),
      },
      safeCurrency
    );
  });

  const categories: Record<string, number> = {};
  const sectionsSet = new Set<string>();
  items.forEach((it) => {
    const cat = it.category || 'DESTACADOS';
    categories[cat] = (categories[cat] || 0) + 1;
    if (it.section) sectionsSet.add(it.section);
  });

  return {
    count: items.length,
    currency: safeCurrency,
    rate_per_1000: safeCurrency === 'ARS' ? 7499.0 : 92.0,
    discount_tiers: [],
    categories,
    sections: Array.from(sectionsSet),
    items,
    isFallback: false,
  };
}

const FORTNITE_API_URL = 'https://fortnite-api.com/v2/shop?language=es';
export const CACHE_KEY = 'gf_shop_raw_cache_v2';
export const CACHE_NAME = 'gf-shop-v1';
export const CACHE_URL = '/__gf_shop_cache__';

export interface ShopFetchResult {
  entries: RawShopEntry[];
  currency: SupportedCurrency;
  isFallback: boolean;
  error?: string | null;
}

export function getLocalCachedShop(): { entries: RawShopEntry[]; timestamp: number } | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed?.entries) && parsed.entries.length > 0) {
        return { entries: parsed.entries, timestamp: parsed.timestamp || 0 };
      }
    }
  } catch {}
  return null;
}

export async function getCacheApiShop(): Promise<{ entries: RawShopEntry[]; timestamp: number } | null> {
  if (typeof window === 'undefined' || !('caches' in window)) return null;
  try {
    const cache = await caches.open(CACHE_NAME);
    const resp = await cache.match(CACHE_URL);
    if (resp) {
      const data = await resp.json();
      const entries = data?.entries || data?.data?.entries || [];
      if (Array.isArray(entries) && entries.length > 0) {
        return { entries, timestamp: data?.timestamp || 0 };
      }
    }
  } catch {}
  return null;
}

export async function saveShopToCache(entries: RawShopEntry[]): Promise<void> {
  if (typeof window === 'undefined' || !Array.isArray(entries) || entries.length === 0) return;
  const payload = { timestamp: Date.now(), entries };
  const str = JSON.stringify(payload);

  try {
    localStorage.setItem(CACHE_KEY, str);
  } catch {}

  if ('caches' in window) {
    try {
      const cache = await caches.open(CACHE_NAME);
      await cache.put(
        CACHE_URL,
        new Response(str, {
          headers: { 'Content-Type': 'application/json' },
        })
      );
    } catch {}
  }
}

export async function fetchRawShopEntries(
  currency: SupportedCurrency = 'ARS',
  options: { signal?: AbortSignal } = {}
): Promise<ShopFetchResult> {
  const safeCurrency = sanitizeCurrency(currency);

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 12000);
    const signal = options.signal || controller.signal;

    // 1. Prioridad: Consultar backend optimizado (/api/shop/raw) que sirve imágenes WebP comprimidas
    try {
      const backendResp = await fetch('/api/shop/raw', {
        method: 'GET',
        headers: { Accept: 'application/json' },
        signal,
      });
      if (backendResp.ok) {
        const rawJson = await backendResp.json();
        const entries: RawShopEntry[] =
          rawJson?.data?.entries || rawJson?.entries || [];

        if (entries.length > 0) {
          clearTimeout(timeoutId);
          await saveShopToCache(entries);
          return { entries, currency: safeCurrency, isFallback: false };
        }
      }
    } catch (backendErr: any) {
      if (backendErr?.name === 'AbortError') throw backendErr;
      console.warn('[shopApi] Endpoint optimizado local no disponible, consultando Fortnite-API directa...', backendErr);
    }

    // 2. Respaldo: Consulta directa a Fortnite-API.com
    const response = await fetch(FORTNITE_API_URL, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal,
    });
    clearTimeout(timeoutId);

    if (response.ok) {
      const data = await response.json();
      const entries: RawShopEntry[] = data?.data?.entries || [];

      if (entries.length > 0) {
        await saveShopToCache(entries);
        return { entries, currency: safeCurrency, isFallback: false };
      }
    }
  } catch (err: any) {
    if (err?.name === 'AbortError') throw err;
    console.warn('[shopApi] Fortnite API direct fetch failed, fallback to cache...', err);
  }

  // 3. Fallback final: rescatar de localStorage o Cache API
  const localCached = getLocalCachedShop();
  if (localCached && localCached.entries.length > 0) {
    return {
      entries: localCached.entries,
      currency: safeCurrency,
      isFallback: true,
      error: 'Mostrando catálogo en caché local',
    };
  }

  const cacheApiData = await getCacheApiShop();
  if (cacheApiData && cacheApiData.entries.length > 0) {
    return {
      entries: cacheApiData.entries,
      currency: safeCurrency,
      isFallback: true,
      error: 'Mostrando catálogo en caché local',
    };
  }

  return {
    entries: [],
    currency: safeCurrency,
    isFallback: true,
    error: 'No se pudo cargar el catálogo de la tienda.',
  };
}
